import { useState } from "react";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import cronstrue from "cronstrue";
import JobRunnerToggleButton from "../job-runner-toggle-button/JobRunnerToggleButton";
import ScheduleDialog from "./ScheduleDialog";
import { JobRunnerTabTypes } from "./JobRunner";
import { JobInfoDTO } from "../../../serviceapi/jobRunnerApi";

interface JobRunnerToolBoxProps {
  tabType: JobRunnerTabTypes;
  isConfigEdited: boolean;
  isCodeEdited: boolean;
  isTemplateInfoEdited: boolean;
  isCodeButtonEnabled: boolean;
  saveTaskConfig: () => void;
  saveTaskCode: () => void;
  saveTemplateInfo: () => void;
  selectedJob: JobInfoDTO | null;
  setIsHistoryDialogVisible: (isHistoryDialogVisible: boolean) => void;
  setIsAssignNewCodeVisible: (isAssignNewCodeVisible: boolean) => void;
  fetchCodeFilesList: () => void;
  openTestDataDialog: () => void;
  openLogsDialog: () => void;
  openAddNewJobDialog: () => void;
  openDeleteJobDialog: () => void;
  runTaskManually: (job: JobInfoDTO) => void;
  handleToggleJobRunner: (jobRunnerId?: string) => void;
  updateJob: (job: JobInfoDTO) => void;
}

const getQuartzCronDescription = (cronExpression: string) => {
  try {
    const parts = cronExpression.split(" ");

    if (parts.length !== 7) {
      return "Invalid Quartz cron expression";
    }

    const unixLikeCron = parts.slice(0, 6).join(" ");

    return cronstrue.toString(unixLikeCron, {
      use24HourTimeFormat: true,
      verbose: true,
      throwExceptionOnParseError: false,
      dayOfWeekStartIndexZero: false,
    });
  } catch (error) {
    return "Invalid Quartz cron expression";
  }
};

const JobRunnerToolBox = ({
  tabType,
  isConfigEdited,
  isCodeEdited,
  isTemplateInfoEdited,
  isCodeButtonEnabled,
  saveTaskConfig,
  saveTaskCode,
  saveTemplateInfo,
  selectedJob,
  fetchCodeFilesList,
  setIsHistoryDialogVisible,
  setIsAssignNewCodeVisible,
  openTestDataDialog,
  openLogsDialog,
  openAddNewJobDialog,
  openDeleteJobDialog,
  runTaskManually,
  handleToggleJobRunner,
  updateJob,
}: JobRunnerToolBoxProps) => {
  const [isScheduleDialogVisible, setIsScheduleDialogVisible] =
    useState(false);

  const handleopenAssignNewCodeDialog = () => {
    fetchCodeFilesList();
    setIsAssignNewCodeVisible(true);
  };

  const confirmRunTask = () => {
    if (!selectedJob) return;
    confirmDialog({
      message: `Are you sure you want to run "${selectedJob.taskTitle}"?`,
      header: "Run Task",
      acceptLabel: "Run",
      rejectLabel: "Cancel",
      accept: () => runTaskManually(selectedJob),
    });
  };

  return (
    <div className="tool-box-container">
        <div className="tool-box-top-container">
          <Button
            className="p-button-success p-button-sm"
            icon="pi pi-plus"
            tooltip="New"
            onClick={openAddNewJobDialog}
          />
          <Button
            disabled={
              tabType === JobRunnerTabTypes.CONFIG
                ? !isConfigEdited
                : tabType === JobRunnerTabTypes.CODE
                ? !isCodeEdited
                : !isTemplateInfoEdited
            }
            icon="pi pi-save"
            className="p-button-primary"
            tooltip="Save"
            onClick={() =>
              tabType === JobRunnerTabTypes.CONFIG
                ? saveTaskConfig()
                : tabType === JobRunnerTabTypes.CODE
                ? saveTaskCode()
                : saveTemplateInfo()
            }
          />
          <Button
            className="p-button-primary"
            icon={"pi pi-history"}
            tooltip="History"
            onClick={() => setIsHistoryDialogVisible(true)}
            disabled={!selectedJob}
          />
          <Button
            className="p-button-primary"
            icon={"pi pi-chart-bar"}
            tooltip="Test data"
            onClick={openTestDataDialog}
            disabled={!selectedJob}
          />
          <Button
            className="p-button-primary"
            icon="pi pi-book"
            tooltip="Logs"
            onClick={openLogsDialog}
            disabled={!selectedJob}
          />
          <Button
            className="p-button-primary"
            icon="pi pi-code"
            tooltip="Assign new code"
            onClick={handleopenAssignNewCodeDialog}
            disabled={!isCodeButtonEnabled}
          />
          <Button
            className="p-button-primary"
            icon="pi pi-trash"
            tooltip="Delete"
            onClick={openDeleteJobDialog}
            disabled={!selectedJob}
          />
        </div>
        <div className="tool-box-top-container">
          <Button
            className="p-button-primary"
            icon="pi pi-play"
            tooltip="Run task manually"
            onClick={confirmRunTask}
            disabled={!selectedJob}
          />
          <Button
            className="p-button-primary"
            icon="pi pi-clock"
            tooltip={
              selectedJob
                ? getQuartzCronDescription(selectedJob.schedule.cron)
                : "Schedule"
            }
            onClick={() => setIsScheduleDialogVisible(true)}
            disabled={!selectedJob}
          />
          {selectedJob && (
            <JobRunnerToggleButton
              key={selectedJob.taskId}
              jobRunner={selectedJob}
              onJobRunnerToggled={(jobRunnerId) =>
                handleToggleJobRunner(jobRunnerId)
              }
            />
          )}
        </div>
      {isScheduleDialogVisible && selectedJob && (
        <ScheduleDialog
          isVisible
          setIsVisible={() => setIsScheduleDialogVisible(false)}
          job={selectedJob}
          updateCurrentJob={(job: JobInfoDTO) => updateJob(job)}
        />
      )}
    </div>
  );
};

export default JobRunnerToolBox;
